<template>
  <section
    v-if="hasContent"
    class="ca-cta-hero ca-bullet-container"
    :aria-label="!isLoading ? content.title || content.label || undefined : undefined"
  >
    <div
      class="ca-cta-hero__card"
      :class="{
        'ca-cta-hero__card--image': hasBackgroundImage,
        'ca-cta-hero__card--loading': isLoading,
      }"
    >
      <img
        v-if="hasBackgroundImage && !isLoading"
        class="ca-cta-hero__background"
        :src="content.imageUrl"
        alt=""
        aria-hidden="true"
        draggable="false"
        @error="imageFailed = true"
      />
      <div
        v-if="hasBackgroundImage && content.blackLayer && !isLoading"
        class="ca-cta-hero__overlay"
        aria-hidden="true"
      ></div>

      <div
        v-if="isLoading"
        class="ca-cta-hero__message ca-cta-hero__message--loading"
        aria-hidden="true"
      >
        <div class="ca-cta-hero__info ca-cta-hero__info--loading">
          <span
            class="ca-cta-hero__skeleton ca-cta-hero__skeleton--label"
          ></span>
          <span
            class="ca-cta-hero__skeleton ca-cta-hero__skeleton--title"
          ></span>
          <span
            class="ca-cta-hero__skeleton ca-cta-hero__skeleton--description"
          ></span>
        </div>
        <span
          class="ca-cta-hero__skeleton ca-cta-hero__skeleton--cta"
        ></span>
      </div>

      <CaModuleMessage
        v-else
        class="ca-cta-hero__message"
        :label="content.label"
        :title="content.title"
        :title-heading-type="content.titleHeadingType"
        :description="content.description"
        :description-heading-type="content.descriptionHeadingType"
        :primary-label="content.cta.label"
        size="M"
        :theme="content.contentTheme"
        @primary-click="onCtaClick"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";

type SitecoreUrlValue =
  | string
  | {
      url?: SitecoreUrlValue;
      href?: SitecoreUrlValue;
      src?: SitecoreUrlValue;
      value?: SitecoreUrlValue;
    };

type CtaHeroAction = {
  label?: string;
  text?: string;
  url?: SitecoreUrlValue;
  href?: SitecoreUrlValue;
  target?: string;
};

type CtaHeroModel = {
  label?: string;
  title?: string;
  description?: string;
  contentTheme?: string;
  titleHeadingType?: string;
  descriptionHeadingType?: string;
  headingLevel?: number | string;
  imageUrl?: SitecoreUrlValue;
  image?: SitecoreUrlValue;
  backgroundImageUrl?: SitecoreUrlValue;
  blackLayer?: boolean;
  showBlackLayer?: boolean;
  loading?: boolean;
  cta?: CtaHeroAction | SitecoreUrlValue | null;
  link?: CtaHeroAction | SitecoreUrlValue | null;
  buttonLabel?: string;
  buttonUrl?: SitecoreUrlValue;
  buttonTarget?: string;
};

const props = defineProps<{
  model?: CtaHeroModel | null;
}>();

const emit = defineEmits<{
  (event: "cta-click", mouseEvent: MouseEvent): void;
}>();

const imageFailed = ref(false);

const content = computed(() => {
  const source = props.model ?? {};
  const ctaSource = source.cta ?? source.link;
  const cta = normalizeCtaAction(ctaSource);

  return {
    label: readText(source.label),
    title: readText(source.title),
    titleHeadingType:
      readText(source.titleHeadingType ?? source.headingLevel) || "H2",
    description: readText(source.description),
    descriptionHeadingType: source.descriptionHeadingType,
    contentTheme: normalizeContentTheme(source.contentTheme),
    imageUrl: readUrl(
      source.imageUrl ?? source.image ?? source.backgroundImageUrl,
    ),
    blackLayer: source.blackLayer === true || source.showBlackLayer === true,
    loading: source.loading === true,
    cta: {
      label: readText(cta.label ?? cta.text ?? source.buttonLabel),
      url: readUrl(
        cta.url ??
          cta.href ??
          source.buttonUrl ??
          (typeof ctaSource === "string" ? ctaSource : ""),
      ),
      target:
        readText(cta.target ?? source.buttonTarget) || "_self",
    },
  };
});

const hasContent = computed(
  () =>
    Boolean(props.model) &&
    (content.value.loading ||
      content.value.label ||
      content.value.title ||
      content.value.description ||
      content.value.imageUrl ||
      content.value.cta.label),
);
const isLoading = computed(() => content.value.loading);
const hasBackgroundImage = computed(
  () => Boolean(content.value.imageUrl) && !imageFailed.value,
);

watch(
  () => content.value.imageUrl,
  () => {
    imageFailed.value = false;
  },
);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object";
}

function readText(value: unknown) {
  if (isRecord(value)) {
    return readText(value.text ?? value.value);
  }

  return String(value ?? "");
}

function readUrl(value: unknown): string {
  if (isRecord(value)) {
    return readUrl(value.url ?? value.href ?? value.src ?? value.value);
  }

  return readText(value);
}

function normalizeCtaAction(value: unknown): CtaHeroAction {
  if (!isRecord(value)) return {};

  const source = isRecord(value.value) ? value.value : value;

  return {
    label: readText(source.label),
    text: readText(source.text),
    url: readUrl(source.url),
    href: readUrl(source.href),
    target: readText(source.target),
  };
}

function normalizeContentTheme(value: unknown) {
  return readText(value).toLowerCase() === "inverted" ? "inverted" : "default";
}

function onCtaClick(event: MouseEvent) {
  emit("cta-click", event);

  const { url, target } = content.value.cta;
  if (!url || event.defaultPrevented) return;

  if (target === "_blank") {
    window.open(url, "_blank", "noopener,noreferrer");
  } else if (target && target !== "_self") {
    window.open(url, target);
  } else {
    window.location.assign(url);
  }
}
</script>

<style scoped lang="scss">
.ca-cta-hero {
  box-sizing: border-box;
  width: 100%;
  padding-block: var(--size-8);
  font-family: inter, sans-serif;
}

.ca-cta-hero *,
.ca-cta-hero *::before,
.ca-cta-hero *::after {
  box-sizing: border-box;
}

.ca-cta-hero__card {
  position: relative;
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
  padding: var(--size-6) var(--size-5);
  border-radius: var(--size-5);
  background-image: linear-gradient(
    86.0195deg,
    var(--ca-color-theme-primary, #066542) 1.1546%,
    var(--ca-color-green-logo, #00965e) 98.438%
  );
}

.ca-cta-hero__card--image {
  background-image: none;
}

.ca-cta-hero__card--loading {
  background-image: linear-gradient(
    to left,
    var(--ca-color-empresas-solarized, #f6f7f2),
    var(--ca-color-empresas-solarized-medium, #edecea)
  );
}

.ca-cta-hero__background,
.ca-cta-hero__overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
}

.ca-cta-hero__background {
  display: block;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
}

.ca-cta-hero__overlay {
  background-color: var(
    --ca-color-neutrals-on-background-60,
    rgba(51, 60, 69, 0.6)
  );
}

.ca-cta-hero__message {
  position: relative;
  z-index: 1;
  width: 100%;
  min-width: 0;
  align-items: center;
  justify-content: center;
  gap: var(--size-3);
  text-align: center;
}

.ca-cta-hero__message :deep(.module-message__label) {
  align-self: center;
}

.ca-cta-hero__message :deep(.module-message__text) {
  gap: var(--size-3);
}

.ca-cta-hero__message :deep(.module-message__actions) {
  justify-content: center;
  margin-top: calc(var(--size-7) - var(--size-3));
}

.ca-cta-hero__info {
  display: flex;
  width: 100%;
  min-width: 0;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--size-3);
}


.ca-cta-hero__message--loading,
.ca-cta-hero__info--loading {
  width: 100%;
}

.ca-cta-hero__message--loading {
  display: flex;
  flex-direction: column;
  gap: var(--size-7);
}

.ca-cta-hero__info--loading {
  gap: var(--size-4);
}

.ca-cta-hero__skeleton {
  display: block;
  flex-shrink: 0;
  border-radius: var(--size-3);
  background-size: 200% 100%;
  animation: ca-cta-hero-shimmer 1.5s linear infinite;
}

.ca-cta-hero__skeleton--label {
  width: 77px;
  height: 38px;
  border-radius: 100px;
  background-image: linear-gradient(
    to left,
    rgba(246, 247, 242, 0.29),
    var(--ca-color-empresas-solarized-dark, #e5e3df)
  );
}

.ca-cta-hero__skeleton--title {
  width: 100%;
  height: var(--size-6);
  background-image: linear-gradient(
    to left,
    var(--ca-color-empresas-solarized, #f6f7f2),
    var(--ca-color-empresas-solarized-dark, #e5e3df)
  );
}

.ca-cta-hero__skeleton--description {
  width: 100%;
  height: 26px;
  background-image: linear-gradient(
    to left,
    rgba(246, 247, 242, 0.29),
    var(--ca-color-empresas-solarized-dark, #e5e3df)
  );
}

.ca-cta-hero__skeleton--cta {
  width: 91px;
  height: var(--size-8);
  background-image: linear-gradient(
    to left,
    var(--ca-color-empresas-solarized, #f6f7f2),
    var(--ca-color-empresas-solarized-dark, #e5e3df)
  );
}

@keyframes ca-cta-hero-shimmer {
  from {
    background-position: 200% 0;
  }

  to {
    background-position: -200% 0;
  }
}

@media (min-width: 768px) {
  .ca-cta-hero__card {
    padding: var(--size-7) var(--size-6);
    border-radius: var(--size-6);
    background-image: linear-gradient(
      81.9366deg,
      var(--ca-color-theme-primary, #066542) 1.1546%,
      var(--ca-color-green-logo, #00965e) 98.438%
    );
  }

  .ca-cta-hero__card--image {
    background-image: none;
  }

  .ca-cta-hero__card--loading {
    padding-top: var(--size-6);
    padding-bottom: var(--size-6);
    background-image: linear-gradient(
      to left,
      var(--ca-color-empresas-solarized, #f6f7f2),
      var(--ca-color-empresas-solarized-medium, #edecea)
    );
  }

}

@media (min-width: 1024px) {
  .ca-cta-hero {
    padding-block: var(--size-11);
  }

  .ca-cta-hero__message {
    gap: var(--size-4);
  }

  .ca-cta-hero__message :deep(.module-message__text) {
    gap: var(--size-4);
  }

  .ca-cta-hero__message :deep(.module-message__actions) {
    margin-top: calc(var(--size-7) - var(--size-4));
  }

  .ca-cta-hero__card {
    padding: var(--size-9);
    background-image: linear-gradient(
      78.3458deg,
      var(--ca-color-theme-primary, #066542) 1.1546%,
      var(--ca-color-green-logo, #00965e) 98.438%
    );
  }

  .ca-cta-hero__card--image {
    background-image: none;
  }

  .ca-cta-hero__card--loading {
    padding-right: var(--size-6);
    padding-left: var(--size-6);
    background-image: linear-gradient(
      to left,
      var(--ca-color-empresas-solarized, #f6f7f2),
      var(--ca-color-empresas-solarized-medium, #edecea)
    );
  }

  .ca-cta-hero__info {
    gap: var(--size-4);
  }

  .ca-cta-hero__skeleton--title {
    height: 60px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .ca-cta-hero__skeleton {
    animation: none;
  }
}
</style>